import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { envConfig } from "~/lib/env-validation";
import { trackEvent } from "~/lib/gtag";
import { useAuth } from "~/lib/hooks/use-auth";

interface WatchButtonProps {
	ico: string;
}

interface WatchlistLimitError {
	error: "WATCHLIST_LIMIT_REACHED";
	limit: number;
	used: number;
	currentPlan: "FREE" | "PREMIUM" | "PRO" | "ENTERPRISE";
}

export function WatchButton({ ico }: WatchButtonProps) {
	const { session, user } = useAuth();
	const queryClient = useQueryClient();
	const [limitError, setLimitError] = useState<WatchlistLimitError | null>(null);

	const statusQuery = useQuery({
		queryKey: ["watch-status", ico],
		queryFn: async () => {
			const res = await fetch(`${envConfig.apiBaseUrl}/watches/${ico}/status`, {
				headers: { Authorization: `Bearer ${session?.access_token}` },
			});
			if (!res.ok) throw new Error("Failed to fetch watch status");
			return res.json() as Promise<{ watching: boolean }>;
		},
		enabled: !!session?.access_token,
	});

	const watchMutation = useMutation({
		mutationFn: async () => {
			const res = await fetch(`${envConfig.apiBaseUrl}/watches`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${session?.access_token}`,
				},
				body: JSON.stringify({ ico }),
			});
			if (res.status === 403) {
				const body = (await res.json()) as WatchlistLimitError;
				if (body.error === "WATCHLIST_LIMIT_REACHED") {
					throw Object.assign(new Error("WATCHLIST_LIMIT_REACHED"), { limit: body });
				}
			}
			if (!res.ok) throw new Error("Failed to watch company");
			return res.json();
		},
		onMutate: async () => {
			setLimitError(null);
			await queryClient.cancelQueries({ queryKey: ["watch-status", ico] });
			const prev = queryClient.getQueryData(["watch-status", ico]);
			queryClient.setQueryData(["watch-status", ico], { watching: true });
			return { prev };
		},
		onError: (err: Error & { limit?: WatchlistLimitError }, _vars, context) => {
			if (context?.prev) {
				queryClient.setQueryData(["watch-status", ico], context.prev);
			}
			if (err.limit) {
				setLimitError(err.limit);
				trackEvent("watchlist_limit_reached", {
					current_plan: err.limit.currentPlan,
					limit: err.limit.limit,
				});
			} else {
				toast.error("Firmu se nepodařilo přidat ke sledování.");
			}
		},
		onSuccess: () => {
			toast.success("Firma byla přidána ke sledování.");
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ["watch-status", ico] });
			queryClient.invalidateQueries({ queryKey: ["watches"] });
		},
	});

	const unwatchMutation = useMutation({
		mutationFn: async () => {
			const res = await fetch(`${envConfig.apiBaseUrl}/watches/${ico}`, {
				method: "DELETE",
				headers: { Authorization: `Bearer ${session?.access_token}` },
			});
			if (!res.ok) throw new Error("Failed to unwatch company");
		},
		onMutate: async () => {
			await queryClient.cancelQueries({ queryKey: ["watch-status", ico] });
			const prev = queryClient.getQueryData(["watch-status", ico]);
			queryClient.setQueryData(["watch-status", ico], { watching: false });
			return { prev };
		},
		onError: (_err, _vars, context) => {
			if (context?.prev) {
				queryClient.setQueryData(["watch-status", ico], context.prev);
			}
			toast.error("Firmu se nepodařilo odebrat ze sledování.");
		},
		onSuccess: () => {
			toast.success("Firma byla odebrána ze sledování.");
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ["watch-status", ico] });
			queryClient.invalidateQueries({ queryKey: ["watches"] });
		},
	});

	if (!user) return null;

	const isWatching = statusQuery.data?.watching ?? false;
	const isPending = watchMutation.isPending || unwatchMutation.isPending;

	const handleClick = () => {
		if (isPending) return;
		if (isWatching) {
			unwatchMutation.mutate();
		} else {
			watchMutation.mutate();
		}
	};

	return (
		<div className="flex flex-col items-end gap-2">
			<button
				type="button"
				onClick={handleClick}
				disabled={isPending || statusQuery.status === "pending"}
				className={`btn btn-sm h-10 gap-1.5 rounded-full ${isWatching ? "btn-soft btn-primary" : "btn-secondary-soft"}`}
				aria-busy={isPending}
			>
				{isPending ? (
					<Loader2 className="h-3.5 w-3.5 animate-spin" />
				) : isWatching ? (
					<Bell className="h-3.5 w-3.5" />
				) : (
					<BellOff className="h-3.5 w-3.5" />
				)}
				{isPending ? "Ukládám…" : isWatching ? "Sleduji" : "Sledovat"}
			</button>

			{limitError && (
				<div className="flex flex-col items-end gap-1 text-right max-w-xs">
					<p className="text-xs text-muted-foreground">
						Dosáhli jste limitu {limitError.limit} sledovaných firem v plánu{" "}
						{limitError.currentPlan}.
					</p>
					<Link to="/cenik" className="text-xs font-medium text-primary hover:underline">
						Upgrade plánu
					</Link>
				</div>
			)}
		</div>
	);
}
